import type { FormProps, RequiredSchema } from './types';
import type { Path } from 'react-hook-form';

import type { ReactElement } from 'react';
import { Children, createElement } from 'react';

import { Controller } from 'react-hook-form';

const IS_WEB = true;
import { useCheckForProperFormUsage } from './hooks';
import { getNextInputName } from './utils';

// Define a type for the props we access on child elements
type ChildProps = {
  name?: string;
  nextInputToFocus?: string;
  onSubmitEditing?: (data?: unknown) => void;
  onBlur?: (e: unknown) => void;
  returnKeyType?: string;
  disabled?: boolean;
};

/**
 * @param props FormProps<Schema>
 * @param props.methods UseFormReturn<Schema>
 * @param props.children ReactElement | ReactElement[]
 * @param props.loggingMode A setting for when helpful logging messages should be displayed in the console @default 'only first render'
 * @param props.shouldAutoFocusNextRegisteredInput @default true [React Native only] Should the Form automatically try to set `onSubmitEditing` of the auto-registered that follows a given input.
 * Note that the `nextInputToFocus` prop passed into a component created by `createHandledFormElement` will override this setting.
 * @returns Fragment of `children` injected with react-hook-form methods
 */
const Form = <Schema extends RequiredSchema>({
  methods,
  children,
  loggingMode = 'only first render',
  shouldAutoFocusNextRegisteredInput = true
}: FormProps<Schema>) => {
  useCheckForProperFormUsage(children, methods, loggingMode);

  const childrenToBeRegistered = Children.map(
    children,
    // Cast to ReactElement to access props
    (child) => (child as ReactElement<ChildProps>).props.name
  ).filter((maybeName) => typeof maybeName === 'string') as string[];

  return (
    <>
      {Children.map(children, (child) => {
        // Cast child to ReactElement with our ChildProps interface
        const typedChild = child as ReactElement<ChildProps>;
        const currentChildName = typedChild.props.name;

        if (!currentChildName) {
          return child;
        }

        const nextInputName = getNextInputName(
          childrenToBeRegistered,
          currentChildName
        ) as Path<Schema>;

        const nextInputToFocus = IS_WEB
          ? undefined
          : (typedChild.props.nextInputToFocus ?? nextInputName);

        // React Native only for automatically focusing the next registered input
        const onSubmitEditing = (data?: unknown) => {
          if (typedChild.props.onSubmitEditing) {
            typedChild.props.onSubmitEditing(data);
          } else if (nextInputToFocus && shouldAutoFocusNextRegisteredInput) {
            methods.setFocus(nextInputToFocus as Path<Schema>);
          }
        };

        // React Native only
        // If there is a returnKeyType passed via props, apply it
        // Otherwise, set to `next` if there is a next input to focus
        const returnKeyType =
          typedChild.props.returnKeyType ??
          (!nextInputName || IS_WEB || !shouldAutoFocusNextRegisteredInput
            ? undefined
            : 'next');

        const errormessage =
          methods.formState.errors[
            currentChildName as keyof typeof methods.formState.errors
          ]?.message;
        const defaultValue =
          methods.formState.defaultValues?.[
            currentChildName as keyof typeof methods.formState.defaultValues
          ];

        return (
          <Controller
            control={methods.control}
            name={currentChildName as Path<Schema>}
            render={({ field: { onBlur, onChange, ...rest } }) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const handleOnBlur = (e: any) => {
                if (typedChild.props.onBlur) {
                  typedChild.props.onBlur(e);
                }

                onBlur();
              };

              const reactNativeProps = !IS_WEB
                ? {
                    onSubmitEditing,
                    onChangeText: onChange,
                    returnKeyType
                  }
                : {};

              return createElement(typedChild.type, {
                ...typedChild.props,
                ...reactNativeProps,
                onBlur: handleOnBlur,
                onChange,
                errormessage,
                defaultValue,
                ...rest,
                disabled: typedChild.props.disabled ?? rest.disabled
              });
            }}
          />
        );
      })}
    </>
  );
};

export { Form };
